/**
 * Typed format failure for a response that cannot be interpreted as the
 * expected contract shape. UI code maps it to the generic friendly error copy;
 * the message must never include payload values.
 */
export class FormatError extends Error {
  constructor(message = 'Malformed API response') {
    super(message)
    this.name = 'FormatError'
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Legacy text fields: keep strings, replace every other scalar with ''. */
export function asText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** Optional text fields: keep strings, replace everything else with null. */
export function asNullableText(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/** String lists keep only string members, preserving order and duplicates. */
export function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

/** Numeric fields accept finite numbers and numeric strings; else null. */
export function asNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** Integer counters accept numeric strings and truncate, like Dart's toInt. */
export function asCount(value: unknown): number {
  const parsed = asNumber(value)
  return parsed === null ? 0 : Math.trunc(parsed)
}

export type ListItemParser<T> = (item: Record<string, unknown>) => T

function errorName(error: unknown): string {
  // Only the constructor/name is safe diagnostics material. Error messages can
  // embed payload strings, so they never reach the log.
  if (error instanceof Error) {
    return error.name || error.constructor?.name || 'Error'
  }
  return 'NonError'
}

function reportSkipped(
  endpoint: string,
  skipped: number,
  names: ReadonlySet<string>,
): void {
  if (!import.meta.env.DEV) return
  const details = names.size > 0 ? ` (${[...names].join(', ')})` : ''
  // Endpoint label, skipped count and error names only: never the item, ids,
  // titles or other payload values.
  console.warn(
    `[kuniri-api] skipped ${skipped} malformed item(s) from ${endpoint}${details}`,
  )
}

/**
 * Tolerant parser for `{ items: [...] }` and raw-array list responses.
 *
 * A non-array value is a format failure for the whole response, exactly like
 * Dart's `FormatException`. Inside a valid array, non-object entries and items
 * rejected by the mapper are skipped so one legacy row cannot break the page.
 * Valid items and their server order are preserved.
 */
export function parseList<T>(
  value: unknown,
  endpoint: string,
  parseItem: ListItemParser<T>,
): T[] {
  if (!Array.isArray(value)) {
    throw new FormatError(`Expected a list from ${endpoint}`)
  }

  const items: T[] = []
  const errorNames = new Set<string>()
  let skipped = 0

  for (const item of value) {
    if (!isRecord(item)) {
      skipped += 1
      continue
    }
    try {
      items.push(parseItem(item))
    } catch (error) {
      skipped += 1
      errorNames.add(errorName(error))
    }
  }

  if (skipped > 0) reportSkipped(endpoint, skipped, errorNames)
  return items
}
